import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Download,
  Upload,
  ShieldCheck,
  AlertCircle,
  Clock,
  RefreshCw,
  Archive,
  History,
} from "lucide-react";

/**
 * Backup & Restore — comprehensive edition.
 *
 * Covers every business-critical collection (products, users, orders, COAs,
 * promo codes, articles, affiliates, invoices, loyalty ledger, addresses,
 * stock alerts). An asyncio task on the backend snapshots the entire
 * database to Object Storage every 30 minutes, with rolling retention of
 * 48 hourly + 14 daily + 8 weekly slots. Admins can also download a portable
 * JSON file, upload one for restore, or restore straight from any stored
 * archive.
 */

const COLLECTIONS = [
  { key: "products", label: "Products" },
  { key: "users", label: "Users" },
  { key: "orders", label: "Orders" },
  { key: "coas", label: "COAs" },
  { key: "promo_codes", label: "Promo codes" },
  { key: "articles", label: "Articles" },
  { key: "loyalty_ledger", label: "Loyalty ledger" },
  { key: "stock_notifications", label: "Stock alerts" },
  { key: "affiliates", label: "Affiliates" },
  { key: "affiliate_commissions", label: "Affiliate commissions" },
  { key: "affiliate_payouts", label: "Affiliate payouts" },
  { key: "invoices", label: "Invoices" },
  { key: "addresses", label: "Address book" },
];

const KIND_LABEL = { hourly: "Hourly", daily: "Daily", weekly: "Weekly", manual: "Manual" };

function relative(iso) {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function humanBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v >= 10 ? 0 : 1)} ${units[i]}`;
}

export default function AdminBackup() {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [archives, setArchives] = useState([]);
  const [restoreResult, setRestoreResult] = useState(null);
  const [selected, setSelected] = useState(() =>
    Object.fromEntries(COLLECTIONS.map((c) => [c.key, true]))
  );
  const [mode, setMode] = useState("merge");
  const [pendingFile, setPendingFile] = useState(null);

  const load = async () => {
    try {
      const [s, l] = await Promise.all([
        api.get("/admin/backup/status"),
        api.get("/admin/backup/list"),
      ]);
      setStatus(s.data);
      setArchives(l.data.archives || []);
    } catch {
      // Non-fatal — the buttons still work.
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  const totalSelected = useMemo(
    () => COLLECTIONS.filter((c) => selected[c.key]).length,
    [selected]
  );

  const toggleAll = (v) =>
    setSelected(Object.fromEntries(COLLECTIONS.map((c) => [c.key, v])));

  const downloadFull = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/backup/full");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      a.href = url;
      a.download = `nebula-full-backup-${stamp}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      const total = Object.values(data.counts || {}).reduce((s, n) => s + n, 0);
      toast.success(`Downloaded backup — ${total} records across ${Object.keys(data.counts || {}).length} collections`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Backup failed");
    } finally {
      setBusy(false);
    }
  };

  const snapshotNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/backup/snapshot-now");
      toast.success(`Archived — ${humanBytes(data.archive.size_bytes)}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Snapshot failed");
    } finally {
      setBusy(false);
    }
  };

  const downloadArchive = async (arch) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/admin/backup/archive/${arch.id}`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = arch.filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${arch.filename}`);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Download failed");
    } finally {
      setBusy(false);
    }
  };

  const restoreArchive = async (arch) => {
    const which = COLLECTIONS.filter((c) => selected[c.key]).map((c) => c.key);
    if (!which.length) {
      toast.error("Select at least one collection to restore.");
      return;
    }
    if (!window.confirm(
      `Restore ${which.length} collection(s) from ${arch.filename} in ${mode.toUpperCase()} mode?\n\n` +
      (mode === "overwrite" ? "OVERWRITE will DELETE current records in these collections and replace them." : "MERGE will upsert records without deleting anything.")
    )) return;
    setBusy(true);
    setRestoreResult(null);
    try {
      const { data } = await api.post("/admin/backup/restore-archive", {
        archive_id: arch.id,
        selected: which,
        mode,
      });
      setRestoreResult(data);
      toast.success("Restore complete — review the report below");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  const chooseFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.json$/i.test(f.name)) {
      toast.error("Please select a .json backup file.");
      return;
    }
    setPendingFile(f);
    toast.success(`Loaded ${f.name} — select collections + restore mode, then click Restore.`);
  };

  const runRestoreFromFile = async () => {
    if (!pendingFile) return;
    const which = COLLECTIONS.filter((c) => selected[c.key]).map((c) => c.key);
    if (!which.length) {
      toast.error("Select at least one collection to restore.");
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(await pendingFile.text());
    } catch {
      toast.error("This file isn't valid JSON.");
      return;
    }
    if (!window.confirm(
      `Restore ${which.length} collection(s) from ${pendingFile.name} in ${mode.toUpperCase()} mode?\n\n` +
      (mode === "overwrite" ? "OVERWRITE will DELETE current records in these collections and replace them." : "MERGE will upsert records without deleting anything.")
    )) return;
    setBusy(true);
    setRestoreResult(null);
    try {
      const { data } = await api.post("/admin/backup/restore-full", {
        ...parsed,
        selected: which,
        mode,
      });
      setRestoreResult(data);
      toast.success("Restore complete — review the report below");
      setPendingFile(null);
    } catch (e2) {
      toast.error(formatApiError(e2?.response?.data?.detail) || "Restore failed");
    } finally {
      setBusy(false);
    }
  };

  const latestHourly = status?.buckets?.hourly?.latest;
  const stale = latestHourly && (Date.now() - new Date(latestHourly.created_at).getTime()) > 2 * 60 * 60 * 1000;

  return (
    <div className="p-8 max-w-5xl">
      <div className="label-eyebrow">Admin · Data safety</div>
      <h1 className="text-3xl font-bold mt-1 tracking-tight">Backup &amp; Restore</h1>
      <p className="text-sm text-[#5E636E] mt-2 max-w-2xl">
        Automatic snapshots of every business-critical collection every 30 minutes.
        Downloads are portable JSON files that can be restored across preview and production.
      </p>

      {/* Auto-backup status */}
      <div
        className={`mt-6 border rounded-lg p-5 ${stale ? "border-[#A30414] bg-[#FEF1F1]" : "border-[#D1D5DB] bg-[#F7F7F8]"}`}
        data-testid="backup-status-card"
      >
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Clock className={`h-5 w-5 ${stale ? "text-[#A30414]" : "text-[#0B6623]"}`} />
            <div>
              <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#5E636E]">
                Auto-backup scheduler
              </div>
              <div className="text-sm font-semibold mt-0.5">
                {status?.scheduler_running ? "Running · every 30 min" : "Not running"}
                {latestHourly && (
                  <span className="ml-2 text-[#5E636E] font-normal">
                    · latest hourly {relative(latestHourly.created_at)}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={load}
              disabled={busy}
              className="text-[12px] font-mono uppercase tracking-[0.14em] text-[#5E636E] hover:text-[#003366] inline-flex items-center gap-1.5"
              data-testid="backup-refresh-btn"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              onClick={snapshotNow}
              disabled={busy}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="backup-snapshot-now-btn"
            >
              <Archive className="h-4 w-4" /> Backup now
            </button>
          </div>
        </div>
        {stale && (
          <div className="mt-3 text-[12px] font-mono text-[#A30414] flex items-center gap-1.5">
            <AlertCircle className="h-3.5 w-3.5" />
            Latest auto-backup is more than 2 hours old — click &quot;Backup now&quot; or check backend logs.
          </div>
        )}
        {status?.buckets && (
          <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3 text-[12px]">
            {["hourly", "daily", "weekly", "manual"].map((k) => {
              const b = status.buckets[k];
              return (
                <div key={k} className="bg-white border border-[#E5E7EB] rounded-md px-3 py-2">
                  <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#5E636E]">{KIND_LABEL[k]}</div>
                  <div className="font-semibold mt-0.5">
                    {b?.latest ? relative(b.latest.created_at) : "—"}
                  </div>
                  <div className="text-[11px] text-[#5E636E] font-mono">
                    {b?.latest ? `${humanBytes(b.latest.size_bytes)}` : "no snapshot"}
                    {b?.cap ? ` · cap ${b.cap}` : ""}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Manual download + upload */}
      <div className="mt-8 grid gap-6 md:grid-cols-2">
        <div className="border border-[#D1D5DB] rounded-lg p-6" data-testid="backup-card-download">
          <ShieldCheck className="h-6 w-6 text-[#0B6623]" />
          <div className="font-semibold mt-3">Download full backup</div>
          <p className="text-[13px] text-[#5E636E] mt-1 leading-relaxed">
            One JSON file containing every collection — products, users, orders, COAs, invoices, promos, affiliates, articles, and more. Password hashes preserved.
          </p>
          <button
            onClick={downloadFull}
            disabled={busy}
            className="btn-primary mt-5 inline-flex items-center gap-2"
            data-testid="backup-download-btn"
          >
            <Download className="h-4 w-4" /> {busy ? "Working…" : "Download full backup"}
          </button>
        </div>

        <div className="border border-[#D1D5DB] rounded-lg p-6" data-testid="backup-card-restore">
          <Upload className="h-6 w-6 text-[#003366]" />
          <div className="font-semibold mt-3">Restore from local file</div>
          <p className="text-[13px] text-[#5E636E] mt-1 leading-relaxed">
            Upload any previously-downloaded backup. Choose which collections to restore and the mode below.
          </p>
          <label
            className={`mt-5 inline-flex items-center gap-2 px-4 py-2.5 border border-[#003366] text-[#003366] font-mono text-[12px] uppercase tracking-[0.16em] rounded-md cursor-pointer hover:bg-[#003366]/5 ${busy ? "opacity-50 pointer-events-none" : ""}`}
            data-testid="backup-restore-label"
          >
            <Upload className="h-4 w-4" /> {pendingFile ? `Loaded: ${pendingFile.name}` : "Choose backup file"}
            <input type="file" accept="application/json,.json" onChange={chooseFile} className="hidden" data-testid="backup-restore-input" />
          </label>
          {pendingFile && (
            <button
              onClick={runRestoreFromFile}
              disabled={busy || totalSelected === 0}
              className="btn-secondary mt-3 inline-flex items-center gap-2"
              data-testid="backup-restore-from-file-btn"
            >
              <RefreshCw className="h-4 w-4" /> Restore {totalSelected} collection(s)
            </button>
          )}
        </div>
      </div>

      {/* Restore controls */}
      <div className="mt-8 border border-[#D1D5DB] rounded-lg p-6" data-testid="restore-controls">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#5E636E]">Restore controls</div>
            <div className="font-semibold text-lg mt-0.5">What to restore &amp; how</div>
          </div>
          <div className="flex gap-2">
            <button
              className="text-[12px] font-mono uppercase tracking-[0.14em] text-[#5E636E] hover:text-[#003366]"
              onClick={() => toggleAll(true)}
              data-testid="restore-select-all"
            >
              Select all
            </button>
            <button
              className="text-[12px] font-mono uppercase tracking-[0.14em] text-[#5E636E] hover:text-[#003366]"
              onClick={() => toggleAll(false)}
              data-testid="restore-select-none"
            >
              Clear
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-2 text-[13px]">
          {COLLECTIONS.map((c) => (
            <label key={c.key} className="inline-flex items-center gap-2 cursor-pointer" data-testid={`restore-toggle-${c.key}`}>
              <input
                type="checkbox"
                className="accent-[#003366]"
                checked={!!selected[c.key]}
                onChange={(e) => setSelected((s) => ({ ...s, [c.key]: e.target.checked }))}
              />
              <span>{c.label}</span>
            </label>
          ))}
        </div>

        <div className="mt-5 border-t border-[#E5E7EB] pt-4">
          <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-[#5E636E]">Restore mode</div>
          <div className="mt-2 flex flex-col md:flex-row gap-3">
            <label className={`flex-1 border rounded-md p-3 cursor-pointer ${mode === "merge" ? "border-[#003366] bg-[#003366]/5" : "border-[#D1D5DB]"}`} data-testid="restore-mode-merge">
              <div className="flex items-center gap-2">
                <input type="radio" checked={mode === "merge"} onChange={() => setMode("merge")} className="accent-[#003366]" />
                <span className="font-semibold text-[13px]">Merge (safe)</span>
              </div>
              <p className="text-[12px] text-[#5E636E] mt-1.5">
                Upsert every record from the backup. Existing records get updated by their key (SKU / email / id). Nothing is deleted.
              </p>
            </label>
            <label className={`flex-1 border rounded-md p-3 cursor-pointer ${mode === "overwrite" ? "border-[#A30414] bg-[#A30414]/5" : "border-[#D1D5DB]"}`} data-testid="restore-mode-overwrite">
              <div className="flex items-center gap-2">
                <input type="radio" checked={mode === "overwrite"} onChange={() => setMode("overwrite")} className="accent-[#A30414]" />
                <span className="font-semibold text-[13px] text-[#A30414]">Overwrite (destructive)</span>
              </div>
              <p className="text-[12px] text-[#5E636E] mt-1.5">
                DROP each selected collection first, then insert everything from the backup. Only use for full disaster recovery.
              </p>
            </label>
          </div>
        </div>
      </div>

      {/* Archive list */}
      <div className="mt-8 border border-[#D1D5DB] rounded-lg" data-testid="archive-list">
        <div className="p-5 border-b border-[#E5E7EB] flex items-center gap-2">
          <History className="h-5 w-5 text-[#003366]" />
          <div className="font-semibold">Stored auto-backups</div>
          <div className="text-[12px] text-[#5E636E] ml-2">
            {archives.length} archive{archives.length === 1 ? "" : "s"} · rolling retention 48 hourly + 14 daily + 8 weekly
          </div>
        </div>
        <div className="max-h-[420px] overflow-auto">
          {archives.length === 0 ? (
            <div className="p-6 text-sm text-[#5E636E]">
              No archives yet. The first hourly snapshot lands about a minute after backend startup,
              or click <b>Backup now</b> above to force one.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="bg-[#F7F7F8] text-[10.5px] uppercase tracking-[0.14em] text-[#5E636E] font-mono">
                <tr>
                  <th className="text-left px-4 py-2">Kind</th>
                  <th className="text-left px-4 py-2">Created</th>
                  <th className="text-left px-4 py-2">Size</th>
                  <th className="text-left px-4 py-2">Records</th>
                  <th className="text-right px-4 py-2">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E5E7EB]">
                {archives.map((a) => {
                  const total = Object.values(a.counts || {}).reduce((s, n) => s + n, 0);
                  return (
                    <tr key={a.id} data-testid={`archive-row-${a.id}`}>
                      <td className="px-4 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded font-mono text-[10.5px] uppercase tracking-[0.14em] ${
                          a.kind === "manual" ? "bg-[#003366] text-white" :
                          a.kind === "weekly" ? "bg-[#0B6623] text-white" :
                          a.kind === "daily" ? "bg-[#F5A623] text-white" :
                          "bg-[#E5E7EB] text-[#5E636E]"
                        }`}>
                          {KIND_LABEL[a.kind] || a.kind}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <div>{new Date(a.created_at).toLocaleString()}</div>
                        <div className="text-[11px] text-[#5E636E]">{relative(a.created_at)}</div>
                      </td>
                      <td className="px-4 py-2 font-mono">{humanBytes(a.size_bytes)}</td>
                      <td className="px-4 py-2 font-mono">{total}</td>
                      <td className="px-4 py-2 text-right">
                        <button
                          onClick={() => downloadArchive(a)}
                          disabled={busy}
                          className="text-[12px] font-mono uppercase tracking-[0.14em] text-[#003366] hover:underline mr-3"
                          data-testid={`archive-download-${a.id}`}
                        >
                          Download
                        </button>
                        <button
                          onClick={() => restoreArchive(a)}
                          disabled={busy || totalSelected === 0}
                          className="text-[12px] font-mono uppercase tracking-[0.14em] text-[#A30414] hover:underline disabled:text-[#A0A4AC]"
                          data-testid={`archive-restore-${a.id}`}
                        >
                          Restore
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Result panel */}
      {restoreResult && (
        <div className="mt-8 border border-[#D1D5DB] rounded-lg p-6 bg-[#F7F7F8]" data-testid="backup-restore-result">
          <div className="flex items-center gap-2 text-[12px] font-mono uppercase tracking-wider text-[#0B6623]">
            <ShieldCheck className="h-3.5 w-3.5" /> Restore complete
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 text-[13px]">
            {Object.entries(restoreResult.report || {}).map(([col, r]) => (
              <div key={col} className="bg-white border border-[#E5E7EB] rounded-md px-3 py-2" data-testid={`restore-report-${col}`}>
                <div className="font-semibold">{col}</div>
                <div className="text-[12px] text-[#5E636E] font-mono mt-1">
                  mode={r.mode} · updated={r.updated ?? 0} · inserted={r.inserted ?? 0} · skipped={r.skipped ?? 0} · in-file={r.records_in_backup ?? 0}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-10 text-[12px] font-mono text-[#5E636E] leading-relaxed">
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-[#5E636E] mb-2">What&apos;s included in every full backup</div>
        products · users (password hashes preserved) · orders · COAs · promo codes · articles ·
        loyalty ledger · stock notifications · affiliates · affiliate commissions · affiliate payouts ·
        invoices · address book
        <div className="mt-3 text-[10.5px] uppercase tracking-[0.18em] text-[#5E636E] mb-2">Not included</div>
        audit logs · rate-limit counters · password-reset tokens (all safe to regenerate)
      </div>
    </div>
  );
}
